<script lang="ts" setup>
import validateFormalizationStep from '~/composables/formalization/useFormalizationStepGuard'
import { FEATURE_FLAGS } from '~/constants'
import { useFeatureFlag } from '~/store-v2/feature-flag/useFeatureFlag'
import useFinancing from '~/store-v2/financings/useFinancing'
import useInstallation from '~/store-v2/installation/useInstallation'
import InstallationCanceled from './_partials/InstallationCanceled.vue'
import NewInstallationExperience from './_partials/new-experience/[id].vue'
import OldInstallationExperience from './_partials/old-experience/[id].vue'

const route = useRoute<any>()
const router = useRouter()
const projectId = computed(() => route.params.id as string)

const {
  data: enableNewInstallation,
  isLoading: isLoadingFlag,
} = useFeatureFlag<boolean>(FEATURE_FLAGS.ENABLE_INSTALLATION_NEW_EXPERIENCE, false, true)

const {
  data: installation,
  isLoading: isLoadingInstallation,
} = useInstallation(projectId)

const {
  data: financing,
  isLoading: isLoadingFinancing,
} = useFinancing(projectId, false)

const isFinancingCanceled = computed(() => financing.value?.status === 'canceled')

const isLoading = computed(() => {
  if (isFinancingCanceled.value && financing.value && !isLoadingFinancing.value)
    return isLoadingFlag.value

  return isLoadingFlag.value || isLoadingInstallation.value || isLoadingFinancing.value
})

watch(financing, async (newFinancing) => {
  if (newFinancing && !isLoadingFinancing.value && newFinancing.status !== 'canceled') {
    await nextTick()
    await validateFormalizationStep(newFinancing, projectId.value, route.path, router)
  }
}, { immediate: true })

// Lógica para decidir qual experiência mostrar caso seja uma instalação concluída na experiência antiga
const shouldShowNewExperience = computed(() => {
  if (!enableNewInstallation.value) {
    return false
  }

  if (isLoadingInstallation.value || !installation.value) {
    return false
  }

  const { status, stage } = installation.value

  if (status.slug === 'success' && stage !== null && stage < 3) {
    return false
  }

  if (status.slug === 'success' && stage !== null && stage >= 3) {
    return true
  }

  return enableNewInstallation.value
})
</script>

<template>
  <!-- loading state -->
  <div v-if="isLoading" class="bg-[#fff] w-full h-[90dvh] flex justify-center items-center">
    <Spinner size="large" />
  </div>

  <InstallationCanceled
    v-else-if="isFinancingCanceled && financing"
    :financing="financing"
    :installation="installation"
  />

  <!-- success state -->
  <div v-else-if="shouldShowNewExperience">
    <NewInstallationExperience />
  </div>

  <div v-else>
    <OldInstallationExperience />
  </div>
</template>
